import BaseClass from 'components/BaseClass';
import React from 'react';
import { wrapComponent } from 'utils/framework';
import InfoButton from 'components/InfoButton/InfoButton';
import { Box } from 'components/ui';
import personalizedPreviewPlacementsApi from 'services/api/personalizedPreviewPlacements';
import VariationCarousel from 'components/PersonalizedPreviewPlacements/VariationCarousel/VariationCarousel';
import userUtils from 'utils/User';
import localeUtils from 'utils/LanguageLocale';
import { DebouncedResize } from 'constants/events';
import contentConstants from 'constants/content';

const { PRIORITIZATION_TYPE } = contentConstants;

const getText = localeUtils.getLocaleResourceFile('components/PersonalizedPreviewPlacements/PersonalizedPlacement/locales', 'PersonalizedPlacement');
class PersonalizedPlacement extends BaseClass {
    state = {
        variations: [], // Store variation IDs
        variationDates: {}, // Store dates for each variation
        models: [],
        isVariationCarouselOpen: false,
        isRuleDetailBoxOpen: false,
        rule: null,
        isPrioritizationTypeStandard: false,
        parentWidth: null,
        contentType: null,
        variationIdsFromDefaultBanners: [],
        lastClickedIndex: null, // Track the last clicked carousel index for duplicate handling
        variationToRuleIdMap: {}
    };

    infoButtonRef = React.createRef();

    setParentWidth = () => {
        this.setState({
            parentWidth: this.infoButtonRef.current?.parentElement?.clientWidth
        });
    };

    setupPersistentBanner = () => {
        if (this.props.isPersistentBanner) {
            window.addEventListener(DebouncedResize, this.setParentWidth);
            this.setParentWidth();
        }
    };

    cleanupPersistentBanner = () => {
        if (this.props.isPersistentBanner) {
            window.removeEventListener(DebouncedResize, this.setParentWidth);
        }
    };

    getPersistentBannerPosition = parentWidth => {
        const iconPlacement = parentWidth - 50;

        return iconPlacement > 1248 ? 1248 : iconPlacement;
    };

    getCurrentVariationDates = () => {
        const { variations, variationDates } = this.state;
        const { nonTargettedVariations } = this.props;
        const activePage = this.props.p13n?.carouselActivePage;
        const nullDates = { startDate: null, endDate: null };
        const isAnonymous = userUtils.isAnonymous();
        const isPromotionalBannerList = this.props.isNBOEnabled && this.props?.wrappedComponentProps?.promo;

        if (isPromotionalBannerList) {
            return this.getLoggedInUserDates(nullDates);
        }

        if (isAnonymous) {
            return this.getAnonymousUserDates({
                variations,
                variationDates,
                nonTargettedVariations,
                activePage,
                nullDates
            });
        }

        return this.getLoggedInUserDates(nullDates);
    };

    getAnonymousUserDates = ({
        variations, variationDates, nonTargettedVariations, activePage, nullDates
    }) => {
        // no carousel
        if (activePage === undefined || activePage === null) {
            return variationDates[variations[0]] || nullDates;
        }

        // no variations or active page is within non-targeted variations
        if (variations.length === 0 || activePage <= nonTargettedVariations.length) {
            return nullDates;
        }

        // Calculate the actual variation index (accounting for non-targeted variations)
        const nonTargetedCount = nonTargettedVariations?.length || 0;
        const variationIndex = activePage - 1 - nonTargetedCount;

        // Get the current variation
        const currentVariation = variations[variationIndex];

        if (currentVariation && variationDates[currentVariation]) {
            return variationDates[currentVariation];
        }

        // Fallback to empty dates if no specific dates found
        return nullDates;
    };

    getLoggedInUserDates = nullDates => {
        const { isNBCEnabled, isNBOEnabled } = this.props;

        // NBC
        if (isNBCEnabled) {
            const { cxsED, cxsSD } = this.props.wrappedComponentProps;

            return { startDate: cxsSD, endDate: cxsED };
        }

        // NBO + promo
        if (isNBOEnabled && this.props.wrappedComponentProps.promo) {
            const { cxsED, cxsSD } = this.props.wrappedComponentProps.promo;

            return { startDate: cxsSD, endDate: cxsED };
        }

        // Regular personalization
        return this.getRegularLoginUserDates(nullDates);
    };

    getRegularLoginUserDates = nullDates => {
        const { variationDates } = this.state;

        return Object.values(variationDates)[0] || nullDates;
    };

    triggerErrorModal = message => {
        this.props.showInfoModal({
            isOpen: true,
            title: 'Error',
            message,
            showCloseButton: true
        });
    };

    extractConditionsFromRule = ruleText => {
        const regex = /\{([^{}]+?)\}/g;
        const conditions = [];
        let match;

        while ((match = regex.exec(ruleText)) !== null) {
            const condition = match[1].trim();
            conditions.push(condition);
        }

        return conditions;
    };

    createRuleObject = data => {
        const conditions = this.extractConditionsFromRule(data?.rule);

        return {
            conj: data?.rootNode?.conj,
            conditions: conditions,
            name: data?.name,
            isPrioritizationTypeStandard: this.state.isPrioritizationTypeStandard,
            dates: {
                startDate: data?.startDate,
                endDate: data?.endDate
            }
        };
    };

    createScoreBasedRule = model => {
        const condition = `model = ${model}`;

        return {
            name: getText('scoreBasedPriority'),
            conditions: [condition],
            isPrioritizationTypeStandard: this.state.isPrioritizationTypeStandard
        };
    };

    processRuleSetData = (data, isAnonymous) => {
        const variations = [];
        const ruleIds = [];
        const models = [];

        data?.rules?.forEach(item => {
            if (isAnonymous) {
                if (!variations.includes(item?.variation)) {
                    variations.push(item?.variation);
                    ruleIds.push(item?.ruleId);
                    models.push(item?.model);
                }
            } else {
                if (item?.ruleId === this.props.personalizedComponent?.ruleId) {
                    variations.push(item?.variation);
                    ruleIds.push(item?.ruleId);
                    models.push(item?.model);
                }
            }
        });

        return { variations, ruleIds, models };
    };

    getRuleSet = isAnonymous => {
        const contextIds = [];

        if (!isAnonymous) {
            if (this.props.isNBCEnabled || (this.props.isNBOEnabled && this.props.wrappedComponentProps.promo)) {
                this.getRuleData('', null);

                return;
            }
        }

        if (this.props.isNBCEnabled && Sephora?.configurationSettings?.isMLEnhancementsPreviewEnabled) {
            // get all context IDs from non-targeted variations
            if (this.props.nonTargettedVariations && this.props.nonTargettedVariations.length > 0) {
                this.props.nonTargettedVariations.forEach(variation => {
                    if (variation?.personalization?.context && !contextIds.includes(variation.personalization.context)) {
                        contextIds.push(variation.personalization.context);
                    }
                });
            }
        } else {
            // isNotAnonymous - case of nbo/nbc banner and nbo
            const contextId = isAnonymous ? this.props.contextId : this.props.personalizedComponent?.context;

            if (!contextId) {
                this.triggerErrorModal(getText('noRuleFound'));

                return;
            }

            contextIds.push(contextId);
        }

        const apiToCall = this.props.isNBCEnabled
            ? personalizedPreviewPlacementsApi.getRuleSetByContextIds
            : personalizedPreviewPlacementsApi.getRuleSetByContextId;
        const param = this.props.isNBCEnabled ? contextIds : contextIds[0];

        apiToCall(param)
            .then(async data => {
                // collect all rules from ruleset and pass to processRuleSetData
                const rules = data.map(item => item.rules).flat();

                const { variations, models } = this.processRuleSetData({ rules }, isAnonymous);
                const firstRuleSet = data[0];

                // Create variation dates mapping and ruleid- variation mapping
                const variationDates = {};
                const variationToRuleIdMap = {};
                data.forEach(ruleset => {
                    if (ruleset?.rules) {
                        ruleset.rules.forEach(rule => {
                            variationToRuleIdMap[rule.variation] = rule.ruleId;

                            if (rule?.variation) {
                                variationDates[rule.variation] = {
                                    startDate: ruleset.startDate,
                                    endDate: ruleset.endDate
                                };
                            }
                        });
                    }
                });

                // If nbo and persistent banner , Extract dynamic banner variations and combine with existing variations
                const variationIdsFromDefaultBanners = await this.extractDynamicBannerVariations(variations, variationToRuleIdMap, data);

                // if bannerpoolid exist and nbc
                const bannerPoolVariations = await this.extractVariationsFromBannerFromPoolId(variationDates, variationToRuleIdMap);
                const finalVariations = [...variationIdsFromDefaultBanners, ...bannerPoolVariations];

                firstRuleSet?.contentType !== 'promotionList' &&
                    this.props.setPersonalizedVariations(
                        this.props.contextId,
                        finalVariations,
                        () => {
                            // api getVariations is called by action setPersonalizedVariations
                            this.triggerErrorModal(getText('noBannerData'));
                            this.setState({ isVariationCarouselOpen: false });
                        },
                        validVariations => {
                            this.setState({
                                variations: validVariations,
                                variationToRuleIdMap,
                                models,
                                variationDates,
                                isVariationCarouselOpen: true, // always true as we want to show rulebox always if not carousel
                                variationIdsFromDefaultBanners: this.props.isNBCEnabled ? variationIdsFromDefaultBanners : null,
                                isPrioritizationTypeStandard: firstRuleSet?.prioritizationType === PRIORITIZATION_TYPE.STANDARD,
                                contentType: firstRuleSet?.contentType || null
                            });
                        }
                    );

                if (!isAnonymous) {
                    this.placementApiCall(firstRuleSet?.rules?.[0]?.ruleId);
                }

                if (firstRuleSet?.contentType === 'promotionList') {
                    this.placementApiCall(firstRuleSet?.rules?.[0]?.ruleId);
                    this.setState({
                        isVariationCarouselOpen: false,
                        contentType: firstRuleSet?.contentType || null
                    });
                }
            })
            .catch(() => {
                this.triggerErrorModal(getText('noRuleSets'));
                this.setState({ isVariationCarouselOpen: false });
            });
    };

    // only for nbc and persistent banner case
    extractDynamicBannerVariations = async (variations, variationToRuleIdMap, data) => {
        if (this.props.wrappedComponentProps.bannerType === 'PersistentBanner' && this.props.wrappedComponentProps.personalization.isNBOEnabled) {
            try {
                const allRulesInfo = data[0]?.rules; // []
                const dynamicBannerRuleInfo = allRulesInfo.find(rule => rule?.variationContentType === 'dynamicBanner');

                if (!dynamicBannerRuleInfo?.ruleId) {
                    return variations;
                }

                const dynamicBannerRuleId = dynamicBannerRuleInfo.ruleId;

                const { country, channel, language } = Sephora.renderQueryParams;
                const bannerArray = await personalizedPreviewPlacementsApi.getVariations({
                    variationIds: variations,
                    channel,
                    language,
                    country
                });

                // if no banners found
                if (!bannerArray?.length) {
                    return variations;
                }

                // Extract all dynamic banners into a separate array
                const dynamicBannerArray = bannerArray.filter(banner => banner?.type === 'DynamicBanner');

                // return variations if no dynamic banners found
                if (dynamicBannerArray.length === 0) {
                    return variations;
                }

                // Find dynamic banner with promotion mappings and extract variation IDs
                const dynamicBannerWithMappings = dynamicBannerArray.find(banner => banner.promotionContainer?.promotionBannerMapping?.length > 0);

                if (!dynamicBannerWithMappings) {
                    return variations;
                }

                const variationIdsFromDynamicBanner = dynamicBannerWithMappings.promotionContainer.promotionBannerMapping
                    .filter(mapping => mapping?.value)
                    .map(mapping => mapping.value);

                // Get sys.id values from all banners for filtering
                const bannerSysIds = new Set(bannerArray.map(banner => banner.sys?.id).filter(Boolean));

                // Filter variations to keep only those that correspond to existing banners
                const filteredVariations = variations.filter(variation => bannerSysIds.has(variation));
                variationIdsFromDynamicBanner.forEach(variation => {
                    variationToRuleIdMap[variation] = dynamicBannerRuleId;
                });

                return [...new Set([...filteredVariations, ...variationIdsFromDynamicBanner])];
            } catch (error) {
                return variations;
            }
        }

        return variations;
    };

    extractVariationsFromBannerFromPoolId = async (variationDates, variationToRuleIdMap) => {
        if (this.props.isNBCEnabled && Sephora?.configurationSettings?.isMLEnhancementsPreviewEnabled) {
            const bannerPoolId = this.props.wrappedComponentProps.bannerPoolId;

            if (!bannerPoolId) {
                return [];
            }

            const { country, channel, language } = Sephora.renderQueryParams;

            // p13n variation api call with bannerPoolId
            let itemsWithVariationId = await personalizedPreviewPlacementsApi.getVariations({
                variationIds: [bannerPoolId],
                channel,
                language,
                country
            });

            itemsWithVariationId = itemsWithVariationId[0].items;
            itemsWithVariationId.forEach(item => {
                variationToRuleIdMap[item.sys?.id] = variationToRuleIdMap[item.sys?.id] ? variationToRuleIdMap[item.sys?.id] : '';
            });
            let variationIdsFromBannerPoolItems = itemsWithVariationId.map(item => {
                return item.sys?.id;
            });

            // steps to get variationId for each personalized banner FromBannerPool
            const contextIdsOfPersonalizedBanner = [];
            itemsWithVariationId.forEach(item => {
                if (item?.personalization?.context) {
                    contextIdsOfPersonalizedBanner.push(item.personalization.context);
                }
            });

            const ruleSetDataOfPersonalizedBanner = await personalizedPreviewPlacementsApi.getRuleSetByContextIds(contextIdsOfPersonalizedBanner);
            ruleSetDataOfPersonalizedBanner.forEach(rulesetObj => {
                if (rulesetObj?.rules) {
                    // we have contextid for each ruleset
                    // find index of this ruleset context [...., ,]
                    const index = variationIdsFromBannerPoolItems.findIndex(variation => variation === rulesetObj.contextId);
                    rulesetObj.rules.forEach(rule => {
                        if (rule?.variation) {
                            if (index >= 0) {
                                variationToRuleIdMap[rule.variation] = rule.ruleId;
                            }

                            // variationId for each personalized banner FromBannerPool
                            const variationId = rule.variation;
                            // how to check which context or variation from variationIdsFromBannerPoolItems this variationId belong to
                            variationIdsFromBannerPoolItems =
                                index !== -1
                                    ? [
                                        ...variationIdsFromBannerPoolItems.slice(0, index + 1),
                                        variationId,
                                        ...variationIdsFromBannerPoolItems.slice(index + 1)
                                    ]
                                    : [...variationIdsFromBannerPoolItems, variationId];
                            variationDates[variationId] = {
                                startDate: rulesetObj.startDate,
                                endDate: rulesetObj.endDate
                            };
                        }
                    });
                }
            });

            return variationIdsFromBannerPoolItems.length > 0 ? [...variationIdsFromBannerPoolItems] : [];
        } else {
            return [];
        }
    };

    getRuleIdForUser = (ruleForLoggedInUser, index) => {
        const isAnonymous = userUtils.isAnonymous();
        const offset = this.props.nonTargettedPreviewIndex ? 0 : this.props.nonTargettedVariations.length;

        if (isAnonymous) {
            const variationIndex = index - 1 - offset;

            return variationIndex >= 0 ? this.state.variationToRuleIdMap[this.state.variations[index - 1 - offset]] : null;
        } else {
            return ruleForLoggedInUser;
        }
    };

    handleRuleDataSuccess = data => {
        const rule = this.createRuleObject(data);

        this.setState({
            rule,
            isVariationCarouselOpen: true,
            isRuleDetailBoxOpen: true
        });
    };

    handleRuleDataError = () => {
        this.triggerErrorModal(getText('noRuleData'));
        this.setState({
            isVariationCarouselOpen: false,
            isRuleDetailBoxOpen: false
        });
    };

    placementApiCall = ruleId => {
        personalizedPreviewPlacementsApi
            .getRuleDetails(ruleId)
            .then(data => {
                if (data?.statusCode === 400) {
                    this.handleRuleDataError();
                } else {
                    this.handleRuleDataSuccess(data);
                }
            })
            .catch(() => {
                this.triggerErrorModal(getText('noRuleData'));
            });
    };

    getRuleData = (ruleForLoggedInUser, index = null) => {
        const activePage = this.props.p13n?.carouselActivePage;

        if (this.props.isNBCEnabled && this.props.nonTargettedVariations && activePage <= this.props.nonTargettedVariations.length) {
            this.setState({
                isVariationCarouselOpen: true,
                isRuleDetailBoxOpen: true
            });

            return;
        }

        if (this.state.isPrioritizationTypeStandard) {
            const ruleId = this.getRuleIdForUser(ruleForLoggedInUser, index);

            this.placementApiCall(ruleId);
        } else {
            const rule = this.createScoreBasedRule(this.state.models[activePage - 1]);

            this.setState({
                rule,
                isVariationCarouselOpen: true,
                isRuleDetailBoxOpen: true
            });
        }
    };

    checkVariationExists = rule => {
        return this.props.p13n.variations?.[this.props.contextId]?.find(item => item?.sys?.id === rule);
    };

    setVariation = rule => {
        const variationData = {
            [this.props.contextId]: rule
        };

        // If rule is null, it means we're resetting - just set the variation to null
        if (!rule) {
            this.props.setActiveVariation(variationData);

            // reset non-targeted preview when resetting variation
            if (this.props.nonTargettedClick) {
                this.props.nonTargettedClick(-1);
            }

            return true;
        }

        const hasVariation = this.checkVariationExists(rule);

        if (!hasVariation && rule) {
            this.triggerErrorModal(getText('noBannerDataForVariation'));
            this.closeVariationCarousel();

            return false;
        }

        this.props.setActiveVariation(variationData);

        return true;
    };

    closeVariationCarousel = () => {
        this.setState({
            isVariationCarouselOpen: false
        });
    };

    closeRuleBox = () => {
        this.setState({
            isRuleDetailBoxOpen: false
        });
    };

    handleInfoButtonClick = () => {
        const { wrappedComponentProps } = this.props;
        const originalBannerSid = this.props.p13n?.originalBannerSid;

        if (wrappedComponentProps?.sid != null && wrappedComponentProps.sid !== originalBannerSid) {
            this.setVariation(null);

            // Dispatch to Redux store
            this.props.setOriginalBannerSid(wrappedComponentProps.sid);
        }

        this.getRuleSet(userUtils.isAnonymous());
    };

    handleNonTargetedItemClick = index => {
        const nonTargetedIndex = index;

        if (this.props.nonTargettedClick) {
            const sidToUse = this.getSidForNonTargetedClick(index);
            this.props.nonTargettedClick(nonTargetedIndex, sidToUse);
        }
    };

    getSidForNonTargetedClick = () => {
        const originalBannerSid = this.props.p13n?.originalBannerSid;

        return originalBannerSid || this.props.sid;
    };

    handleTargetedItemClick = index => {
        const targetedIndex = index - (this.props.nonTargettedVariations?.length || 0);
        const variationIsSet = this.setVariation(this.state.variations[targetedIndex - 1]);

        if (this.props.nonTargettedClick) {
            this.props.nonTargettedPreviewIndex !== -1 && this.props.nonTargettedClick(-1);
        }

        const activePage = this.props.p13n?.carouselActivePage;

        if (activePage !== targetedIndex && this.state.isRuleDetailBoxOpen && variationIsSet) {
            this.getRuleData('', targetedIndex);
        }
    };

    // targetted and non targetted item click handler
    handleCarouselItemClick = index => {
        // Store the clicked index for carousel display
        this.setState({ lastClickedIndex: index });
        // Also store in Redux so Banner components can access it
        this.props.setCarouselActivePage(index);

        if (this.props.nonTargettedVariations && index <= this.props.nonTargettedVariations?.length) {
            this.handleNonTargetedItemClick(index);
        } else {
            this.handleTargetedItemClick(index);
        }
    };

    componentDidMount() {
        this.setupPersistentBanner();
        this.props.setSidData(this.props.sid);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.sid !== this.props.sid) {
            this.props.updateSidData({
                prevSid: prevProps.sid,
                currentSid: this.props.sid
            });
        }
    }

    componentWillUnmount() {
        this.cleanupPersistentBanner();
    }

    renderInfoButton = () => {
        const { isPersistentBanner } = this.props;
        const persistentBannerLeft = this.getPersistentBannerPosition(this.state.parentWidth);

        return (
            <InfoButton
                buttonType='personalizedInfo'
                position='absolute'
                top={!isPersistentBanner && 12}
                left={isPersistentBanner ? persistentBannerLeft : 12}
                bottom={isPersistentBanner && 2}
                size={[24, 34]}
                onClick={this.handleInfoButtonClick}
            />
        );
    };

    renderVariationCarousel = () => {
        if (!this.state.isVariationCarouselOpen) {
            return null;
        }

        const nonTargetted = this.props.nonTargettedVariations ?? [];
        const allVariations = [...nonTargetted];
        const isNotInitialLoadOnIconClick = this.state.lastClickedIndex && this.state.lastClickedIndex >= 0;

        const activeKey = Object.keys(this.props.p13n?.activeVariations)[0];

        if (this.props.p13n?.variations?.[activeKey]) {
            allVariations.push(this.props.p13n?.variations[activeKey] || []);
        }

        // Calculate the correct SID using proper array indexing
        let sidToCarousel = this.props.sid; // Default fallback

        if (this.props.isNBCEnabled && isNotInitialLoadOnIconClick) {
            const currentActivePage = this.state.lastClickedIndex || (this.props.activePage ?? 0) + 1;
            // Convert 1-based activePage to 0-based array index
            const arrayIndex = currentActivePage - 1;

            if (arrayIndex >= 0 && arrayIndex < allVariations.length) {
                const currentVariation = allVariations[arrayIndex];
                sidToCarousel = currentVariation?.sid || this.props.sid;
            }
        }

        return (
            <VariationCarousel
                sid={sidToCarousel}
                onClose={() => {
                    this.closeVariationCarousel();
                    this.closeRuleBox();
                }}
                position={this.infoButtonRef.current?.getBoundingClientRect()}
                nonTargettedPreviewIndex={this.props.nonTargettedPreviewIndex}
                isNBCEnabled={this.props.isNBCEnabled}
                variations={allVariations}
                contentType={this.state.contentType}
                isNBOEnabled={this.props.isNBOEnabled}
                dividerIndex={nonTargetted.length}
                activePage={this.state.lastClickedIndex || this.props.activePage}
                variationIdsFromDefaultBanners={this.state.variationIdsFromDefaultBanners}
                resetVariation={() => {
                    this.closeRuleBox();
                    this.setVariation(null);
                    this.setState({ lastClickedIndex: null });
                    this.props.setCarouselActivePage(0);
                }}
                viewRuleClick={(user, index) => this.getRuleData(user, index)}
                onClick={this.handleCarouselItemClick}
                isRuleDetailBoxOpen={this.state.isRuleDetailBoxOpen}
                rule={{ ...this.state.rule, dates: this.getCurrentVariationDates() }}
                OnRuleClose={this.closeRuleBox}
            />
        );
    };

    render() {
        return (
            <Box
                position='relative'
                zIndex='1000'
                ref={this.infoButtonRef}
            >
                {this.renderInfoButton()}
                {this.renderVariationCarousel()}
            </Box>
        );
    }
}

export default wrapComponent(PersonalizedPlacement, 'PersonalizedPlacement');
